/**
 * 标准配合求解器调试追踪。
 *
 * 文件职责：
 * 1. 在显式开启时输出求解器 plain-data 输入、几何摘要、诊断与图求解事件；
 * 2. 为复杂装配闭环问题提供可复制的结构化日志；
 * 3. 不依赖 Viewer、UI、Object3D 或浏览器专有类型，避免调试能力污染求解职责。
 */

import {
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../definition';
import type { ComponentTransformSnapshot } from '../component-instance';
import {
  resolveAxisGeometry,
  resolveConeGeometry,
  resolvePlaneGeometry,
  resolvePointGeometry,
  resolveSphereGeometry,
  type AxisGeometry,
} from './geometry/StandardMateGeometryAccess';
import type {
  StandardMateSolverDebugTraceOptions,
  StandardMateSolverInput,
  StandardMateSolverResult,
} from './StandardMateSolverTypes';

/** 浏览器或宿主环境中用于开启求解器追踪的全局键。 */
export const STANDARD_MATE_SOLVER_DEBUG_TRACE_GLOBAL_KEY = '__PUPPYCAD_STANDARD_MATE_SOLVER_TRACE__';

/** 浏览器或宿主环境中缓存最近追踪快照的全局键。 */
export const STANDARD_MATE_SOLVER_DEBUG_TRACE_LOGS_GLOBAL_KEY = '__PUPPYCAD_STANDARD_MATE_SOLVER_TRACE_LOGS__';

/** 全局追踪快照最多保留数量，避免长时间拖拽产生无上限内存增长。 */
const MAX_GLOBAL_TRACE_LOG_COUNT = 50;

/**
 * 数值输出小数位数。
 *
 * 用 12 位以保证追踪快照可**高保真回放**：残差失败发生在 ~1e-7 量级，6 位（1e-6）会把残差抹平，
 * 无法离线复现“报了 solved 却仍越界”的边界态。12 位对位姿（米级）给到 1e-12 分辨率，足够忠实且不失真。
 */
const TRACE_NUMBER_PRECISION = 12;

/** 模块内自增 ID，便于区分同一毫秒内的多次求解。 */
let standardMateSolverDebugTraceSequence = 0;

/**
 * 单个调试追踪事件。
 */
export interface StandardMateSolverDebugTraceEvent {
  /** 事件阶段。 */
  readonly phase: string;
  /** 阶段数据。 */
  readonly detail: unknown;
}

/**
 * 调试追踪会话。
 */
export interface StandardMateSolverDebugTraceSession {
  /** 追踪 ID。 */
  readonly traceId: string;
  /** 是否输出 solved 结果。 */
  readonly includeSolved: boolean;
  /** 自定义输出钩子。 */
  readonly emit?: (snapshot: unknown) => void;
  /** 最终输出的 payload。 */
  readonly payload: {
    readonly schemaVersion: 1;
    readonly traceId: string;
    readonly timestamp: string;
    readonly label: string;
    readonly input: unknown;
    readonly events: StandardMateSolverDebugTraceEvent[];
    final?: unknown;
  };
}

/**
 * 约束图转动副调试摘要。
 */
export interface StandardMateRevoluteJointDebugSummary {
  /** 形成转动副的同轴心 Mate ID。 */
  readonly concentricMateId: string;
  /** 第一组件 ID。 */
  readonly firstComponentId: string;
  /** 第二组件 ID。 */
  readonly secondComponentId: string;
  /** 第一端轴线摘要。 */
  readonly firstAxis: unknown;
  /** 第二端轴线摘要。 */
  readonly secondAxis: unknown;
}

/**
 * 读取可写全局记录。
 *
 * @returns 全局记录对象。
 */
function readGlobalRecord(): Record<string, unknown> {
  return globalThis as Record<string, unknown>;
}

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 未知值。
 * @returns 是否为记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/**
 * 将布尔或对象形式的追踪配置规范化。
 *
 * @param value 原始配置。
 * @returns 追踪配置；显式禁用时返回 null。
 */
function normalizeDebugTraceOptions(
  value: boolean | StandardMateSolverDebugTraceOptions | unknown,
): StandardMateSolverDebugTraceOptions | null {
  if (value === true) {
    return { enabled: true };
  }
  if (value === false || value === null || value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    return null;
  }
  if (value.enabled === false) {
    return null;
  }
  return {
    enabled: true,
    label: typeof value.label === 'string' ? value.label : undefined,
    includeSolved: typeof value.includeSolved === 'boolean' ? value.includeSolved : undefined,
    emit: typeof value.emit === 'function'
      ? value.emit as (snapshot: unknown) => void
      : undefined,
  };
}

/**
 * 从输入或全局开关中解析追踪配置。
 *
 * @param input 求解器输入。
 * @returns 追踪配置；未开启时返回 null。
 */
function resolveDebugTraceOptions(input: StandardMateSolverInput): StandardMateSolverDebugTraceOptions | null {
  if (input.debugTrace !== undefined) {
    return normalizeDebugTraceOptions(input.debugTrace);
  }
  return normalizeDebugTraceOptions(readGlobalRecord()[STANDARD_MATE_SOLVER_DEBUG_TRACE_GLOBAL_KEY]);
}

/**
 * 将数值规整为稳定输出。
 *
 * @param value 原始数值。
 * @returns 规整后的数值或字符串。
 */
function formatTraceNumber(value: number): number | string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  const scale = 10 ** TRACE_NUMBER_PRECISION;
  return Math.round(value * scale) / scale;
}

/**
 * 克隆三元数组。
 *
 * @param value 三元数组。
 * @returns 输出数组。
 */
function formatTuple3(value: readonly [number, number, number]): readonly [number | string, number | string, number | string] {
  return [
    formatTraceNumber(value[0]),
    formatTraceNumber(value[1]),
    formatTraceNumber(value[2]),
  ] as const;
}

/**
 * 克隆四元数组。
 *
 * @param value 四元数组。
 * @returns 输出数组。
 */
function formatTuple4(
  value: readonly [number, number, number, number],
): readonly [number | string, number | string, number | string, number | string] {
  return [
    formatTraceNumber(value[0]),
    formatTraceNumber(value[1]),
    formatTraceNumber(value[2]),
    formatTraceNumber(value[3]),
  ] as const;
}

/**
 * 输出 transform 摘要。
 *
 * @param transform transform 快照。
 * @returns 可序列化 transform 摘要。
 */
function summarizeTransform(transform: ComponentTransformSnapshot | undefined): unknown {
  if (!transform) {
    return null;
  }
  return {
    position: formatTuple3(transform.position),
    quaternion: formatTuple4(transform.quaternion),
    scale: formatTuple3(transform.scale),
  };
}

/**
 * 输出 Vector3 摘要。
 *
 * @param value Vector3。
 * @returns 三元数组。
 */
function summarizeVector3(value: { readonly x: number; readonly y: number; readonly z: number }): readonly [number | string, number | string, number | string] {
  return [
    formatTraceNumber(value.x),
    formatTraceNumber(value.y),
    formatTraceNumber(value.z),
  ] as const;
}

/**
 * 输出轴线摘要。
 *
 * @param axis 轴线几何。
 * @returns 可序列化轴线摘要。
 */
export function summarizeAxisForStandardMateDebugTrace(axis: AxisGeometry | null | undefined): unknown {
  if (!axis) {
    return null;
  }
  return {
    origin: summarizeVector3(axis.origin),
    direction: summarizeVector3(axis.direction),
    radius: axis.radius === undefined ? undefined : formatTraceNumber(axis.radius),
  };
}

/**
 * 输出配合实体摘要。
 *
 * @param entity 配合实体。
 * @returns 可序列化实体摘要。
 */
export function summarizeMateEntityForStandardMateDebugTrace(entity: MateEntityRef | undefined): unknown {
  if (!entity) {
    return null;
  }
  const axis = resolveAxisGeometry(entity);
  const plane = resolvePlaneGeometry(entity);
  const cone = resolveConeGeometry(entity);
  const point = resolvePointGeometry(entity);
  const sphere = resolveSphereGeometry(entity);

  return {
    componentInstanceId: entity.componentInstanceId,
    assemblyNodeId: entity.assemblyNodeId,
    entityKind: entity.entityKind,
    stableRef: entity.stableRef,
    label: entity.label,
    topologyRef: entity.topologyRef ?? null,
    referenceRef: entity.referenceRef ?? null,
    componentTransformAtSelection: summarizeTransform(entity.componentTransformAtSelection),
    componentWorldTransformAtSelection: summarizeTransform(entity.componentWorldTransformAtSelection),
    geometry: {
      rawEntityKind: entity.geometry.entityKind,
      axis: summarizeAxisForStandardMateDebugTrace(axis),
      plane: plane
        ? {
            point: summarizeVector3(plane.point),
            normal: summarizeVector3(plane.normal),
          }
        : null,
      cone: cone
        ? {
            axis: summarizeAxisForStandardMateDebugTrace(cone),
            apex: summarizeVector3(cone.apex),
            halfAngle: formatTraceNumber(cone.halfAngle),
          }
        : null,
      point: point ? summarizeVector3(point.point) : null,
      sphere: sphere
        ? {
            center: summarizeVector3(sphere.center),
            radius: formatTraceNumber(sphere.radius),
          }
        : null,
    },
  };
}

/**
 * 输出 Mate Definition 摘要。
 *
 * @param mate Mate Definition。
 * @param transientMateIds 临时 Mate ID 集合。
 * @returns 可序列化 Mate 摘要。
 */
export function summarizeMateForStandardMateDebugTrace(
  mate: AssemblyMateDefinition,
  transientMateIds: ReadonlySet<string> = new Set<string>(),
): unknown {
  return {
    mateId: mate.mateId,
    mateType: mate.mateType,
    suppressed: mate.suppressed,
    role: transientMateIds.has(mate.mateId) ? 'transient' : 'committed',
    alignment: mate.alignment,
    dimension: mate.dimension ?? null,
    lockRotation: mate.lockRotation ?? false,
    concentricMisalignment: mate.concentricMisalignment ?? null,
    firstEntity: summarizeMateEntityForStandardMateDebugTrace(mate.firstEntity),
    secondEntity: summarizeMateEntityForStandardMateDebugTrace(mate.secondEntity),
    referenceEntity: summarizeMateEntityForStandardMateDebugTrace(mate.referenceEntity),
  };
}

/**
 * 输出组件摘要。
 *
 * @param input 求解器输入。
 * @returns 可序列化组件摘要。
 */
function summarizeComponents(input: StandardMateSolverInput): unknown {
  return Object.values(input.components).map((component) => ({
    componentInstanceId: component.componentInstanceId,
    fixationState: component.fixationState,
    transform: summarizeTransform(component.transform),
    worldTransform: summarizeTransform(component.worldTransform),
    worldBounds: component.worldBounds ?? null,
  }));
}

/**
 * 输出求解器输入摘要。
 *
 * @param input 求解器输入。
 * @returns 可序列化输入摘要。
 */
function summarizeSolverInput(input: StandardMateSolverInput): unknown {
  const transientMateIds = new Set(input.transientMateIds ?? []);
  return {
    targetMateId: input.targetMateId ?? null,
    transientMateIds: [...transientMateIds],
    drivenComponentInstanceIds: [...(input.drivenComponentInstanceIds ?? [])],
    previewAnchorComponentInstanceIds: [...(input.previewAnchorComponentInstanceIds ?? [])],
    distanceBranchPolicy: input.distanceBranchPolicy ?? null,
    useTransientInitialPlacement: input.useTransientInitialPlacement ?? null,
    linearDimensionScale: input.linearDimensionScale ?? null,
    epsilon: input.epsilon ?? null,
    angleEpsilon: input.angleEpsilon ?? null,
    components: summarizeComponents(input),
    mateDefinitions: input.mateDefinitions.map((mate) => summarizeMateForStandardMateDebugTrace(mate, transientMateIds)),
  };
}

/**
 * 输出求解结果摘要。
 *
 * @param result 求解结果。
 * @returns 可序列化结果摘要。
 */
function summarizeSolverResult(result: StandardMateSolverResult): unknown {
  return {
    status: result.status,
    componentTransforms: Object.fromEntries(
      Object.entries(result.componentTransforms).map(([componentId, transform]) => [
        componentId,
        summarizeTransform(transform),
      ]),
    ),
    diagnostics: result.diagnostics.map((diagnostic) => ({ ...diagnostic })),
  };
}

/**
 * 创建调试追踪会话。
 *
 * @param input 求解器输入。
 * @returns 追踪会话；未开启时返回 null。
 */
export function createStandardMateSolverDebugTraceSession(
  input: StandardMateSolverInput,
): StandardMateSolverDebugTraceSession | null {
  const options = resolveDebugTraceOptions(input);
  if (!options) {
    return null;
  }

  standardMateSolverDebugTraceSequence += 1;
  const traceId = `standard-mate-solver:${Date.now()}:${standardMateSolverDebugTraceSequence}`;
  return {
    traceId,
    includeSolved: options.includeSolved ?? false,
    emit: options.emit,
    payload: {
      schemaVersion: 1,
      traceId,
      timestamp: new Date().toISOString(),
      label: options.label ?? 'standard-mate-solver',
      input: summarizeSolverInput(input),
      events: [],
    },
  };
}

/**
 * 追加调试事件。
 *
 * @param session 追踪会话。
 * @param phase 事件阶段。
 * @param detail 阶段数据。
 */
export function appendStandardMateSolverDebugTraceEvent(
  session: StandardMateSolverDebugTraceSession | null | undefined,
  phase: string,
  detail: unknown,
): void {
  if (!session) {
    return;
  }
  session.payload.events.push({
    phase,
    detail,
  });
}

/**
 * 写入全局追踪缓存。
 *
 * @param payload 追踪 payload。
 */
function appendGlobalTracePayload(payload: unknown): void {
  const globalRecord = readGlobalRecord();
  const existingLogs = globalRecord[STANDARD_MATE_SOLVER_DEBUG_TRACE_LOGS_GLOBAL_KEY];
  const logs = Array.isArray(existingLogs) ? existingLogs : [];
  logs.push(payload);
  if (logs.length > MAX_GLOBAL_TRACE_LOG_COUNT) {
    logs.splice(0, logs.length - MAX_GLOBAL_TRACE_LOG_COUNT);
  }
  globalRecord[STANDARD_MATE_SOLVER_DEBUG_TRACE_LOGS_GLOBAL_KEY] = logs;
}

/**
 * 向控制台输出追踪 payload。
 *
 * @param payload 追踪 payload。
 */
function emitTracePayloadToConsole(payload: unknown): void {
  console.info('[PuppyCAD StandardMateSolver Trace]', payload);
  console.info('[PuppyCAD StandardMateSolver Trace JSON]', JSON.stringify(payload));
}

/**
 * 完成并输出调试追踪。
 *
 * @param session 追踪会话。
 * @param finalResult 最终求解结果。
 */
export function emitStandardMateSolverDebugTrace(
  session: StandardMateSolverDebugTraceSession | null | undefined,
  finalResult: StandardMateSolverResult,
): void {
  if (!session) {
    return;
  }
  if (finalResult.status === MateSolveStatus.Solved && !session.includeSolved) {
    return;
  }

  session.payload.final = summarizeSolverResult(finalResult);
  appendGlobalTracePayload(session.payload);
  session.emit?.(session.payload);
  emitTracePayloadToConsole(session.payload);
}
